import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NumberValueAccessor } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AuthService } from '@app/core/auth';
import { environment } from '@app/env-service';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  url :string = environment.url;

  curPipe :CurrencyPipe;
  datePipe :DatePipe;

  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  loading$ = this._loading.asObservable();

  constructor(private http :HttpClient) {
    this.curPipe = new CurrencyPipe('en');
    this.datePipe = new DatePipe('en');
  }

  get(params) {
    return this.http.get(`${this.url}/inventory`, { params: params });
  }

  export(params :any) :Observable<any> {
    return this.http.get(`${this.url}/inventory`, { params: params });
  }

  getQtyLogs(params) {
    return this.http.get(`${this.url}/inventory/logs`, { params: params });
  }

  getKitLogs(params) {
    return this.http.get(`${this.url}/inventory/logs`, { params: params });
  }

  postAdjustments(adjustmentEntries :any) :Observable<any> {
    return this.http.post(`${this.url}/inventory/adjustments`, adjustmentEntries);
  }

  getAdjustmentEntries(params :any) :Observable<any> {
    return this.http.get(`${this.url}/inventory/adjustments`, {
      params: params,
    });
  }

  mergeStocks(payload :any) :Observable<any> {
    return this.http.post(`${this.url}/inventory/merge_stocks`, payload);
  }

  addStockLocations(stockId :number, payload :any) :Observable<any> {
    return this.http.post(`${this.url}/inventory/${stockId}/locations`, payload);
  }

  addSkuToStockLocation(locationId :number, skuId :number) :Observable<any> {
    return this.http.put(
      `${this.url}/inventory/locations/${locationId}/skus/${skuId}`,
      {},
    );
  }

  removeStockLocation(stockId :number, locationId :number) :Observable<any> {
    return this.http.delete(
      `${this.url}/inventory/${stockId}/locations/${locationId}`,
      {},
    );
  }

  removeStockLocationLot(
    stockId :number,
    locationId :number,
    lotId ?:number | null,
  ) :Observable<any> {
    if (lotId) {
      return this.http.delete(
        `${this.url}/inventory/${stockId}/locations/${locationId}/lot/${lotId}`,
        {},
      );
    } else {
      return this.http.delete(
        `${this.url}/inventory/${stockId}/locations/${locationId}/lot`,
        {},
      );
    }
  }

  updateStockWarehouse(stockId :number, payload :any) :Observable<any> {
    return this.http.put(`${this.url}/inventory/${stockId}/warehouses`, payload);
  }

  createStockEntry(payload :any) :Observable<any> {
    return this.http.post(`${this.url}/inventory`, payload);
  }

  recountPending() :Observable<any> {
    return this.http.put(`${this.url}/inventory/recount-pending`, {});
  }

  moveSkusFromLocationToLocation(
    rfqStockLocationIds :number[],
    sourceLocationId,
    destinationLocationId,
    quantity :number,
  ) :Observable<any> {
    return this.http.post(`${this.url}/inventory/transfer-skus-location`, {
      rfqStockLocationIds,
      sourceLocationId,
      destinationLocationId,
      quantity,
    });
  }

  bulkMoveSkusToNewLocation(payload :any) :Observable<any> {
    this._loading.next(true);

    return this.http
      .post(`${this.url}/inventory/bulk-transfer-skus-location`, payload)
      .pipe(
        map((response) => {
          this._loading.next(false);

          return response;
        }),
        catchError((response) => {
          this._loading.next(false);

          return throwError(response.error);
        }),
      );
  }
}
